import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './components/ThemeProvider';
import { TaskProvider } from './lib/context/TaskContext';
import { DashboardLayout } from './components/layout/DashboardLayout';
import CookieBanner from './components/CookieBanner';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import Dashboard from './pages/Dashboard';
import Performance from './pages/Performance';
import Messaging from './pages/Messaging';
import Calendar from './pages/Calendar';
import Contacts from './pages/Contacts';
import Library from './pages/Library';
import BrandCampaignQuiz from './pages/BrandCampaignQuiz';
import ProductCampaignQuiz from './pages/ProductCampaignQuiz';
import FollowerCampaignQuiz from './pages/FollowerCampaignQuiz';
import CommunityBuilder from './pages/CommunityBuilder';
import GiveawayCampaign from './pages/GiveawayCampaign';
import EngagementQuiz from './pages/EngagementQuiz';
import CampaignGenerator from './pages/CampaignGenerator';
import Repurpose from './pages/Repurpose';
import CreatorAssistant from './pages/CreatorAssistant';
import LearningHub from './pages/LearningHub';
import MultiPlatformComposer from './pages/MultiPlatformComposer';
import AITools from './pages/AITools';
import HashtagGenerator from './pages/ai-tools/HashtagGenerator';
import ReelScriptGenerator from './pages/ai-tools/ReelScriptGenerator';
import SEOCaptionAnalyzer from './pages/ai-tools/SEOCaptionAnalyzer';
import PostIdeaBrainstormer from './pages/ai-tools/PostIdeaBrainstormer';
import CarouselOutlineBuilder from './pages/ai-tools/CarouselOutlineBuilder';
import EmailDraftGenerator from './pages/ai-tools/EmailDraftGenerator';
import ABHookTester from './pages/ai-tools/ABHookTester';
import Subscription from './pages/Subscription';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Settings from './pages/Settings';
import OnboardingQuiz from './pages/OnboardingQuiz';
import Documentation from './pages/Documentation/index';
import { useSupabaseAuth } from './lib/hooks/useSupabase';
import { supabase } from './lib/supabase';

// Global debug flag
window.__DEBUG_POSTING = true;

function App() {
  const { isAuthenticated, isLoading, error } = useSupabaseAuth();
  const [quizCompleted, setQuizCompleted] = useState<boolean | null>(null);

  // Check quiz completion status on mount and auth state changes from database
  useEffect(() => {
    if (isAuthenticated) {
      // First check local storage for performance
      const localQuizStatus = localStorage.getItem('quizCompleted');
      
      if (localQuizStatus === 'true') {
        setQuizCompleted(true);
      } else {
        // If not found in local storage or is false, check database
        checkQuizCompletionInDatabase();
      }
    }
  }, [isAuthenticated]);

  // Function to check quiz completion in database
  const checkQuizCompletionInDatabase = async () => {
    try {
      const { data: preferences, error } = await supabase
        .from('user_preferences')
        .select('*')
        .maybeSingle();
      
      if (error) throw error;
      
      // If user has preferences in the database, they've completed the quiz
      if (preferences) {
        localStorage.setItem('quizCompleted', 'true');
        setQuizCompleted(true);
      } else {
        // No preferences found, quiz not completed
        localStorage.setItem('quizCompleted', 'false');
        setQuizCompleted(false);
      }
    } catch (err) {
      console.error('Error checking quiz completion:', err);
      // Default to not completed if there's an error
      setQuizCompleted(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
        <div className="bg-white dark:bg-dark-800 p-6 rounded-lg shadow-lg max-w-md w-full">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Authentication Error</h2>
          <p className="text-gray-600 dark:text-gray-400">{error.message}</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="mt-4 w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <TaskProvider>
      <Router className="overflow-x-hidden w-full max-w-[100vw]">
        <div className="w-full max-w-[100vw]">
          <Toaster position="top-right" />
          <CookieBanner />
          <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route 
            path="/login" 
            element={isAuthenticated ? <Navigate to="/dashboard\" replace /> : <Login />} 
          />
          <Route 
            path="/signup" 
            element={isAuthenticated ? <Navigate to="/dashboard\" replace /> : <SignUp />} 
          />

          {/* Onboarding Quiz */}
          <Route
            path="/onboarding-quiz"
            element={
              isAuthenticated ? (
                quizCompleted ? <Navigate to="/dashboard\" replace /> : <OnboardingQuiz />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* Protected routes with DashboardLayout */}
          <Route
            path="/dashboard"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <Dashboard />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/performance"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <Performance />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/messaging"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <Messaging />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/calendar"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <Calendar />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/contacts"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <Contacts />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/library"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <Library />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/campaign-generator"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <CampaignGenerator />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/repurpose"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <Repurpose />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/creator-assistant"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <CreatorAssistant />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/multi-platform-composer"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <MultiPlatformComposer />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/ai-tools"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <AITools />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/ai-tools/hashtag-generator"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <HashtagGenerator />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/ai-tools/reel-script-generator"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <ReelScriptGenerator />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/ai-tools/seo-caption-analyzer"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <SEOCaptionAnalyzer />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/ai-tools/post-idea-brainstormer"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <PostIdeaBrainstormer />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/ai-tools/carousel-outline-builder"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <CarouselOutlineBuilder />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/ai-tools/email-draft-generator"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <EmailDraftGenerator />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/ai-tools/ab-hook-tester"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <ABHookTester />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/learning-hub"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <LearningHub />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/subscription"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <Subscription />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/settings"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <Settings />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/engagement-quiz"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <EngagementQuiz />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/follower-campaign"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <FollowerCampaignQuiz />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/product-campaign"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <ProductCampaignQuiz />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/brand-campaign"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <BrandCampaignQuiz />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/community-campaign"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <CommunityBuilder />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/giveaway-campaign"
            element={
              isAuthenticated ? (
                quizCompleted === false ? (
                  <Navigate to="/onboarding-quiz\" replace />
                ) : (
                  <DashboardLayout>
                    <GiveawayCampaign />
                  </DashboardLayout>
                )
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* Documentation */}
          <Route
            path="/docs/*"
            element={
              <DashboardLayout>
                <Documentation />
              </DashboardLayout>
            }
          />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* Catch all route - redirect to landing page */}
          <Route path="*" element={<Navigate to="/\" replace />} />
          </Routes>
        </div>
      </Router>
      </TaskProvider>
    </ThemeProvider>
  );
}

export default App;